import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/auth/useAuth';
import { useAuthStore } from '../stores/auth-store';
import { useAvailableSocieties, AvailableSocietyAction } from '../hooks/societies/useAvailableSocieties';
import { getEnvironmentConfig } from '@/config/domainConfigLoader';

type SocietyOptionKey = 'canCreateContract' | 'deliveryProofCreate' | 'presaleSheet';

const SOCIETY_OPTION_TO_ACTION: Record<SocietyOptionKey, AvailableSocietyAction> = {
  canCreateContract: 'contract',
  deliveryProofCreate: 'deliveryProof',
  presaleSheet: 'presaleSheet',
};

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAll?: boolean;
  requiredSocietyOption?: SocietyOptionKey;
}

export const ProtectedRoute = ({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
  requiredSocietyOption,
}: ProtectedRouteProps) => {
  const { isAuthenticated, hasPermission, hasAnyPermission } = useAuth();
  const { isAuthReady, currentUser, logout, refreshToken } = useAuthStore();
  const location = useLocation();

  // Logique permissive multi-société : l'accès est autorisé dès qu'AU MOINS une
  // société assignée à l'external user active l'option requise.
  const shouldCheckSocietyOption = !!requiredSocietyOption && currentUser?.type === 'EXTERNAL';
  const action: AvailableSocietyAction | null = requiredSocietyOption
    ? SOCIETY_OPTION_TO_ACTION[requiredSocietyOption]
    : null;
  const { societies: eligibleSocieties, isLoading: isConfigLoading } = useAvailableSocieties(
    action ?? 'contract',
  );

  // Bloquer les utilisateurs INTERNAL → rediriger vers l'admin avec transfert de session
  const isInternalUser = currentUser?.type === 'INTERNAL';
  useEffect(() => {
    if (isAuthReady && isAuthenticated && isInternalUser) {
      const adminUrl = getEnvironmentConfig().ADMIN_URL;
      if (adminUrl) {
        const token = refreshToken;
        logout();
        const base = adminUrl.startsWith('http') ? adminUrl : `https://${adminUrl}`;
        const separator = base.includes('?') ? '&' : '?';
        window.location.href = token ? `${base}${separator}authTransfer=${encodeURIComponent(token)}` : base;
      }
    }
  }, [isAuthReady, isAuthenticated, isInternalUser, logout, refreshToken]);

  if (!isAuthReady) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Préserver la destination à travers le login (la LoginPage lit `returnUrl`
    // et y navigue après connexion). Indispensable pour les liens profonds des
    // emails, ex. validation garant `/sav/:id?redirect_to_validation=true`.
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  if (isInternalUser) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary" />
      </div>
    );
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/403" replace />;
  }

  if (requiredPermissions) {
    const hasAccess = requireAll
      ? requiredPermissions.every(p => hasPermission(p))
      : hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
      return <Navigate to="/403" replace />;
    }
  }

  // Vérifier l'option société (uniquement pour les utilisateurs EXTERNAL)
  // Accès autorisé dès qu'au moins une société assignée active l'option.
  if (shouldCheckSocietyOption && requiredSocietyOption) {
    if (isConfigLoading) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
          <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary" />
        </div>
      );
    }
    if (eligibleSocieties.length === 0) {
      return <Navigate to="/403" replace />;
    }
  }

  return <>{children}</>;
};
